import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  EmptyStateProps,
  Pagination,
  TabItem,
  Tabs,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
} from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";
import { errorText } from "../../../lib/http";
import BillingHistoryTable from "../components/BillingHistoryTable";
import UserClientsTable from "../components/UserClientsTable";
import UserContactCard from "../components/UserContactCard";
import UserDetailSkeleton from "../components/UserDetailSkeleton";
import UserProfileCard from "../components/UserProfileCard";
import useUserDetail, { DETAIL_ROWS_PER_PAGE } from "../hooks/useUserDetail";
import { downloadInvoice } from "../services/usersService";
import type { Invoice, UserDetailTabId } from "../types/users.types";

const TABS: TabItem<UserDetailTabId>[] = [
  { id: "details", label: "Details" },
  { id: "billing", label: "Billing history" },
];

/** An unknown or missing `?tab=` opens on Details, rather than on nothing. */
const tabFromParam = (value: string | null): UserDetailTabId =>
  TABS.some((tab) => tab.id === value) ? (value as UserDetailTabId) : "details";

/**
 * One Naurist account, in full.
 *
 * Two tabs: who they are and what they run, then what they have paid. The
 * screen reads and never writes — there is nothing here that changes a
 * customer's record, which is why there is no Edit anywhere on it.
 */
const UserDetailPage: React.FC = () => {
  // The param is the enciphered token `userDetailPath` wrote, not the id
  // itself. A token that does not decode — mistyped, truncated in a chat, or
  // made before `REACT_APP_ROUTE_ID_SECRET` last changed — comes back as "",
  // which `useUserDetail` reports as a missing account.
  const { userId: token = "" } = useParams();
  const decoded = decodeRouteId(token);
  const userId = decoded && /^\d+$/.test(decoded) ? Number(decoded) : null;

  // In the URL rather than in state, so the tab survives a refresh and can be
  // linked to. Replaced rather than pushed: Back should leave the account, not
  // walk back through every tab that was looked at along the way.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabFromParam(searchParams.get("tab"));
  const setActiveTab = (tab: UserDetailTabId) =>
    setSearchParams({ tab }, { replace: true });

  // A page apiece, kept separately so leaving a tab and coming back lands
  // where it was left rather than at the top of a list somebody had paged into.
  const [clientPage, setClientPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);
  const page = activeTab === "details" ? clientPage : invoicePage;

  const { user, clients, invoices, total, status, reload } = useUserDetail(
    userId,
    activeTab,
    page
  );

  // Read before the returns below, since a hook cannot sit behind one.
  const phase = useLoadPhase(status === "loading");

  /**
   * Where the open tab's rows are.
   *
   * `total` is null until the view on screen has been answered for — see
   * `useUserDetail` — which is exactly the window in which its table has
   * nothing yet. Without this the other tab opens on its empty state: the
   * billing rows are genuinely `[]` before their first fetch, and "Nothing
   * billed yet" is what an empty array means once you have asked. It is not
   * what it means before.
   *
   * The tables show rows they already hold while a refetch runs, so coming
   * back to a tab shows the previous page rather than shimmering again.
   */
  const rowsPhase = useLoadPhase(total === null);

  // What went wrong with a download, said where the reader is looking. Also
  // the one place fixture mode admits it has no files — see `downloadInvoice`.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState("");

  // `total` is the count for whichever view is open, so this is the page count
  // for the table on screen — there is no second list being paged behind it.
  const totalPages = Math.max(1, Math.ceil((total ?? 0) / DETAIL_ROWS_PER_PAGE));

  const handleDownload = async (invoice: Invoice) => {
    if (userId === null) return;

    setDownloadingId(invoice.id);
    setDownloadError("");

    try {
      await downloadInvoice(userId, invoice);
    } catch (error) {
      setDownloadError(
        errorText(error, "We could not fetch that invoice. Please try again.")
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // The account drawn as blocks while it is fetched, but only once the fetch
  // has visibly stalled. One that arrives quickly opens on itself, with no
  // screenful of blocks in between.
  //
  // Only the first load of all: once the account is in hand, switching tabs or
  // turning a page fetches again, and replacing the whole screen with blocks
  // each time would make a tab change look like a fresh arrival.
  if (status === "loading" && !user) return <UserDetailSkeleton phase={phase} />;

  // A failed load is not a missing account, and saying so would send somebody
  // looking for a record that is sitting there behind an error. Only the
  // failure is worth retrying — a 404 will keep being a 404.
  //
  // The two failures and nothing else. This used to read `status !== "ready"`,
  // which swept up `loading` as well: switching tabs put the screen into a
  // fetch with the account still in hand, and every one of them flashed "That
  // user no longer exists" until the answer came back. A request in flight is
  // not a verdict, and only a verdict belongs on this screen.
  if (status === "failed" || status === "notFound" || !user) {
    const failed = status === "failed";

    return (
      <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        <PageHeader
          title={failed ? "Could not load this user" : "User not found"}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center rounded-md border border-line bg-white">
          <EmptyState
            {...(failed
              ? failedEmptyState(
                  "We could not reach the server",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>
                )
              : notFoundEmptyState(
                  "That user no longer exists",
                  "The account may have been closed. Everyone else on the list is unaffected.",
                  // A link and not a button: this goes somewhere, so it should
                  // open in a new tab on a middle click and be announced as
                  // the way out that it is.
                  <Link
                    to={ROUTES.users}
                    className={buttonClasses({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Back to users
                  </Link>
                ))}
          />
        </div>
      </div>
    );
  }

  /** What each table shows instead of rows.
   *
   *  One case each now, where there used to be two. A list can no longer fail
   *  on its own: it arrives in the same answer as the account above it, so a
   *  failure takes the whole screen to the state further up and never reaches
   *  a table. What is left is the honest emptiness. */
  const clientsEmptyState = (): EmptyStateProps => ({
    title: "No clients yet",
    description: `${user.name} has not added anyone to their practice.`,
  });

  const invoicesEmptyState = (): EmptyStateProps => ({
    title: "Nothing billed yet",
    description:
      user.plan === "Free trial"
        ? "This account is still inside its free trial, so it has never been invoiced."
        : "Invoices will appear here from the first payment onwards.",
  });

  return (
    // Same full-height shell as the list: the page never scrolls as a page,
    // only the column of cards inside it does.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title={user.name} />

      <div className="mt-5 flex-shrink-0 border-b border-line">
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <div className="mt-5 flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
        {activeTab === "details" ? (
          <div className="flex flex-col gap-4 pb-2">
            <UserProfileCard user={user} />
            <UserContactCard user={user} />

            <section className="rounded-md border border-line bg-white overflow-hidden">
              <div className="p-4 sm:p-6">
                <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
                  {/* The server's count for the whole roster, not the length of
                      the page on screen — this heading says how many clients
                      the practice has, and the ten rows below it are one page
                      of them. */}
                  Total clients{total === null ? "" : ` (${total})`}
                </h2>
              </div>

              {total === null && (
                <p role="status" className="sr-only">
                  Loading clients…
                </p>
              )}

              <UserClientsTable
                clients={clients}
                empty={clientsEmptyState()}
                phase={rowsPhase}
              />

              {totalPages > 1 && (
                <Pagination
                  currentPage={clientPage}
                  totalPages={totalPages}
                  onPageChange={setClientPage}
                  resultCount={clients.length}
                />
              )}
            </section>
          </div>
        ) : (
          <section className="rounded-md border border-line bg-white overflow-hidden">
            <div className="p-4 sm:p-6">
              <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
                Billing history{total === null ? "" : ` (${total})`}
              </h2>

              {downloadError && (
                <p
                  role="alert"
                  className="mt-2 text-[13px] text-[#D14343] leading-[1.6]"
                >
                  {downloadError}
                </p>
              )}
            </div>

            {total === null && (
              <p role="status" className="sr-only">
                Loading billing history…
              </p>
            )}

            <BillingHistoryTable
              invoices={invoices}
              empty={invoicesEmptyState()}
              downloadingId={downloadingId}
              onDownload={handleDownload}
              phase={rowsPhase}
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={invoicePage}
                totalPages={totalPages}
                onPageChange={setInvoicePage}
                resultCount={invoices.length}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;
